import { useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';

/**
 * Simplified Authentication Token Hook (Microsoft Best Practices)
 * 
 * This hook follows Microsoft's recommended patterns:
 * - Uses MSAL's built-in token management
 * - Leverages automatic refresh and caching
 * - Removes complex retry logic that causes race conditions
 * - Single source of truth for authentication state
 */
export const useAuthToken = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get valid token using MSAL's built-in mechanisms
  const getValidToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) {
      console.log('🚪 User not authenticated - no token available');
      return null;
    }

    const activeAccount = instance.getActiveAccount();
    if (!activeAccount) {
      console.log('⚠️ No active account found');
      return null;
    }

    try {
      setIsLoading(true);
      console.log('🔄 Acquiring token silently...');

      // Use MSAL's built-in silent token acquisition
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: activeAccount,
      });

      if (response.accessToken) {
        console.log('✅ Token acquired successfully');
        setAccessToken(response.accessToken);
        return response.accessToken;
      }

      console.warn('⚠️ No access token in response');
      return null;

    } catch (error: any) {
      console.error('❌ Token acquisition failed:', {
        error: error.errorCode || error.message,
        account: activeAccount.username
      });
      
      // Clear token on error
      setAccessToken(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [instance, isAuthenticated]);

  // Auto-acquire token when authenticated
  useEffect(() => {
    if (isAuthenticated && instance.getActiveAccount()) {
      getValidToken();
    } else if (!isAuthenticated) {
      // Clear token when not authenticated
      setAccessToken(null);
    }
  }, [isAuthenticated, instance, getValidToken]);

  // Clear all authentication state
  const clearAllAuthState = useCallback(() => {
    console.log('🧹 Clearing authentication state...');
    setAccessToken(null);
    setIsLoading(false);
  }, []);

  return { 
    accessToken, 
    isLoading,
    getValidToken,
    clearAllAuthState,
    isTokenValid: () => Boolean(accessToken && isAuthenticated)
  };
};
